import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import {
  Button,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import {
  selectUnidadeSelecionada,
  selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { generateAguardandoChegadaReport } from './AguardandoChegadaPage.helpers';
import QCXSelectViaTransporteAutocomplete from '../../../../shared-components/select-via-transporte/QCXSelectViaTransporteAutocomplete';
import QCXSelectEmbarcacaoAutocomplete from '../../../../shared-components/select-embarcacao/QCXSelectEmbarcacaoAutocomplete';
import { fetchByFilter } from '../../../../features/follow-up/followUpAPI';

interface ControlledValue {
  id: string | number;
  value: string;
}

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '50vw',
      minHeight: '40vh',
    },
  },
});

const AguardandoChegadaPage = ({
  title,
  isOpen,
  closeModal,
}: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  keycloak.updateToken(300);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [businessError, setBusinessError] = useState(false);
  const [datas, setDatas] = useState({
    dataInicio: '',
    dataFim: '',
  });
  const [followUpList, setFollowUpList] = useState<ControlledValue[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<ControlledValue[]>([]);
  const [selectedVia, setSelectedVia] = useState<any>(null);
    const [isFetchingProcesses, setIsFetchingProcesses] = useState<boolean>(false);
  const [selectedEmbarcacao, setSelectedEmbarcacao] = useState<any>(null);
  useUnidadeNegocioGuard();

  const handleTenantsChange = (
    _e: any,
    value: SimpleMultiSelectOption[] | null
  ) => setSelectedTenants(value || []);

  const fetchFilteredFollowUps = useCallback(async (clients: ControlledValue[]) => {
        try {
            setIsFetchingProcesses(true);
            const clientIds = clients.map((client) => client.id).join(',');
            if (clientIds) {
                const response = await fetchByFilter([
                    { name: 'clientIds', value: clientIds },
                    { name: 'incluirVinculadosDi', value: 'true' },
                ]);
                const followUps = response.data.map((item: any) => ({
                    id: item.id?.toString() || '',
                    value: item.numero?.toString() || '[Sem número de processo]',
                }));
                setFollowUpList(followUps);
            } else {
                setFollowUpList([]);
            }
        } catch (error) {
            console.error('Erro ao buscar processos:', error);
            setFollowUpList([]);
        } finally {
            setIsFetchingProcesses(false);
        }
    }, []);

  const handleClientsChange = (
          event: SyntheticEvent<Element, Event>,
          value: { id: string | number | undefined; value: string | undefined }[] | null
      ) => {
          const newSelectedClients: ControlledValue[] = [];
          value?.forEach((val) => {
              const foundClient = clientes.find((c) => c.id === val.id);
              if (foundClient && foundClient.id) {
                  if (!newSelectedClients.some((cli) => cli.id === foundClient.id)) {
                      newSelectedClients.push({
                          id: foundClient.id.toString(),
                          value: `(${foundClient.pessoa?.cnpj}) ${foundClient.pessoa?.nome}`,
                      });
                  }
              }
          });
          setSelectedClients(newSelectedClients);
          setSelectedProcesses([]);
          fetchFilteredFollowUps(newSelectedClients);
      };
  
      const handleProcessesChange = (
          event: SyntheticEvent<Element, Event>,
          value: { id: string | number | undefined; value: string | undefined }[] | null
      ) => {
          const newSelectedProcesses: ControlledValue[] = [];
          value?.forEach((val) => {
              const foundProcess = followUpList.find((f) => f.id === val.id);
              if (foundProcess) {
                  newSelectedProcesses.push(foundProcess);
              }
          });
          setSelectedProcesses(newSelectedProcesses);
      };

  const handleChangeDataInicio = (iso: string) =>
    setDatas((p) => ({ ...p, dataInicio: iso.split('T')[0] }));

  const handleChangeDataFim = (iso: string) =>
    setDatas((p) => ({ ...p, dataFim: iso.split('T')[0] }));

  const validarDatas = () =>
    datas.dataInicio !== '' &&
    datas.dataFim !== '' &&
    new Date(datas.dataInicio) <= new Date(datas.dataFim);

  const handleSubmit = async () => {
    keycloak.updateToken(300);

    if (selectedTenants.length === 0) {
      setBusinessError(true);
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade.' }));
      return;
    }
    setBusinessError(false);

    if (token && selectedUnit) {
      const tenantIds = selectedTenants.map((t) => t.id.toString());
      const clientIds = selectedClients.map((c) => c.id.toString());

      const erro = await generateAguardandoChegadaReport(
        token,
        selectedUnit,
        tenantIds,
        clientIds,
        selectedProcesses.map(p => p.id.toString()),   // ⭕

        selectedVia?.id?.toString() || null,          // ⭕

        selectedEmbarcacao?.id?.toString() || null,   // ⭕

        datas.dataInicio,
        datas.dataFim
      );

      if (erro == null) {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
      } else {
        dispatch(setErrorFeedback({ message: erro }));
      }
    }

    history.push('/importacao/relatorios');
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form onSubmit={() => { }}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={businessError}>
                    <MultiSelectStyled
                      controlledValues={selectedTenants}
                      options={tenantList.map((t: { id: { toString: () => any; }; pessoa: { nomeResumido: any; }; }) => ({
                        id: t.id.toString(),
                        value: t.pessoa.nomeResumido,
                      }))}
                      onChangeAction={handleTenantsChange}
                      label="Unidade de Negócio"
                    />
                    {selectedTenants.length === 0 && (
                      <Typography variant="body2" color="error">
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedClients}
                      options={clientes.map((c) => ({
                        id: c.id ?? '',
                        value: `(${c.pessoa!.cnpj}) ${c.pessoa!.nome}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      disablePortal={false}
                      options={followUpList.map((f) => ({ id: f.id, value: f.value }))}
                      controlledValues={selectedProcesses}
                      onChangeAction={handleProcessesChange}
                      label="Processos"
                      disabled={selectedClients.length === 0 || isFetchingProcesses}
                      placeholder={
                        selectedClients.length === 0
                          ? 'Selecione um Cliente'
                          : isFetchingProcesses
                            ? 'Carregando...'
                            : 'Selecionar Processos'
                      }
                    />
                  </FormControl>
                </Grid>

                {/* Via Transporte */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                    <QCXSelectViaTransporteAutocomplete
                      label="Via Transporte"
                      name="viaTransporte"
                      initialValues={selectedVia}
                      onChange={(_: React.ChangeEvent<{}>, v: any) => setSelectedVia(v)}
                    />
                    </FormControl>
                </Grid>

                {/* Embarcação */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                    <QCXSelectEmbarcacaoAutocomplete
                      label="Embarcação"
                      name="embarcacao"
                      hideCode
                      initialValues={selectedEmbarcacao}
                      onChange={(_: React.ChangeEvent<{}>, v: any) => setSelectedEmbarcacao(v)}
                    />
                    </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="startDate"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="aguardando-chegada-start"
                        name={input.name}
                        label="Previsão de Chegada Inicio"
                        required={false}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangeDataInicio(d.toISOString());
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="endDate"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="aguardando-chegada-end"
                        name={input.name}
                        label="Previsão de Chegada Fim"
                        required={false}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangeDataFim(d.toISOString());
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={4}>
          <Button variant="outlined" color="primary" onClick={closeModal}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            fullWidth
            disabled={
              selectedTenants.length === 0
            }
          >
            Gerar Relatório
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AguardandoChegadaPage;
